import {
  createContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import { useDesktopMenuContext } from 'App/components/Menu/DesktopMenuProvider';
import useNavegate from 'App/hooks/useNavegate/useNavegate';
import { useResponsive } from 'commons/components';
import { AgentChatActionBus } from 'commons/services/AgentChatService';
import {
  AgentChatPanelContextType,
  AgentChatPanelContextProviderProps,
} from './types';

const defaultContextValue: AgentChatPanelContextType = {
  isOpen: false,
  openAgent: () => void 0,
  closeAgent: () => void 0,
  actionBus: null,
};

export const AgentChatPanelContext =
  createContext<AgentChatPanelContextType>(defaultContextValue);

/**
 * AgentChatPanelContextProvider manages the global state of the AI chat side panel and the action bus.
 * This context controls when the chat panel is open/closed and provides the AgentChatActionBus instance to all child components.
 * The action bus is only instantiated on desktop platform.
 */
export function AgentChatPanelContextProvider({
  children,
}: AgentChatPanelContextProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { isMobile } = useResponsive();
  const { goTo } = useNavegate();
  const busRef = useRef<AgentChatActionBus | null>(null);
  const menuStateBeforeAgentChatRef = useRef<boolean | null>(null);
  const { isExpandedMenu, expandMenu, collapseMenu } = useDesktopMenuContext();

  const openAgent = useCallback(() => {
    menuStateBeforeAgentChatRef.current = isExpandedMenu;
    collapseMenu();
    setIsOpen(true);
  }, [isExpandedMenu, collapseMenu]);

  const closeAgent = useCallback(() => {
    if (menuStateBeforeAgentChatRef.current !== null) {
      if (menuStateBeforeAgentChatRef.current) {
        expandMenu();
      }
      menuStateBeforeAgentChatRef.current = null;
    }
    setIsOpen(false);
  }, [expandMenu]);

  if (!busRef.current && !isMobile) {
    busRef.current = new AgentChatActionBus();
    busRef.current.register('navigation', {
      onReplace: (url: string) => {
        goTo(url);
      },
    });
  }

  // Cleanup when mobile (not yet supported) and when dismounted
  if (isMobile && busRef.current) {
    busRef.current.dispose();
    busRef.current = null;
  }

  useEffect(
    () => () => {
      busRef.current?.dispose();
      busRef.current = null;
    },
    [],
  );

  // Ensure the action bus is only available on desktop
  const actionBus = isMobile ? null : busRef.current;

  const value = useMemo(
    () => ({
      isOpen,
      openAgent,
      closeAgent,
      actionBus,
    }),
    [isOpen, openAgent, closeAgent, actionBus],
  );

  return (
    <AgentChatPanelContext.Provider value={value}>
      {children}
    </AgentChatPanelContext.Provider>
  );
}
